import { apiClient, ApiError } from './client';
import type { components } from './schema';

/** One thing waiting for the signed-in person. A value it has none of is null, never missing. */
export type WorkItem = components['schemas']['MyWorkItem'];

/** Everything of one kind that is waiting: how many in all, and the most urgent few. */
export type WorkSection = components['schemas']['MyWorkSection'];

/** The signed-in person's waiting work. */
export type MyWork = components['schemas']['MyWork'];

/** Just the total, for the badge on Today. */
export type MyWorkCount = components['schemas']['MyWorkCount'];

export type WorkSectionKey = WorkSection['key'];

/**
 * The sections of a person's work, most urgent first.
 *
 * The contract's own order. The interface follows it whatever order the server sends, so a section
 * is never promoted by the order it happens to arrive in.
 */
export const WORK_SECTION_ORDER = [
  'SAFETY',
  'APPROVALS',
  'LEADS_TO_ROUTE',
  'DEALS_OVERDUE',
  'DEALS_DUE_TODAY',
  'CONFIRM_SALE',
  'PRESENT_PROPOSAL',
  'HAND_OFF',
  'MARK_INSTALLED',
  'FOLLOW_UPS',
  'TECH_HANDOFFS',
  'TECH_REVIEWS',
  'UNASSIGNED_TODAY',
  'ON_HOLD',
  'READY_TO_INVOICE',
  'INVOICES_TO_SEND',
  'OVERDUE_INVOICES',
  'PAYROLL',
  'LOW_STOCK',
] as const satisfies readonly WorkSectionKey[];

/**
 * Whether a section key is one the contract names.
 *
 * A section a newer server adds is still shown, plainly, so a key is checked rather than trusted.
 *
 * @param key a key from the server
 * @returns true for the nineteen the interface knows how to word
 */
export function isWorkSectionKey(key: string): key is WorkSectionKey {
  return (WORK_SECTION_ORDER as readonly string[]).includes(key);
}

/** The server's own explanation from a problem body, or the status when it gave none. */
function failure(problem: unknown, status: number): ApiError {
  const detail =
    typeof problem === 'object' && problem !== null && 'detail' in problem
      ? problem.detail
      : undefined;
  return new ApiError(
    status,
    typeof detail === 'string' && detail.trim() !== ''
      ? detail
      : `The request failed (${String(status)})`,
  );
}

/**
 * Reads what is waiting for the signed-in person.
 *
 * @returns the sections that have something in them, with the most urgent items of each
 * @throws ApiError when the server refuses
 */
export async function fetchMyWork(): Promise<MyWork> {
  const { data, error, response } = await apiClient.GET('/api/me/work');
  if (data === undefined) {
    throw failure(error, response.status);
  }
  return data;
}

/**
 * Reads how much is waiting, without the items.
 *
 * @returns the total across every section
 * @throws ApiError when the server refuses
 */
export async function fetchMyWorkCount(): Promise<MyWorkCount> {
  const { data, error, response } = await apiClient.GET('/api/me/work/count');
  if (data === undefined) {
    throw failure(error, response.status);
  }
  return data;
}
